'use client';

import { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { motion } from 'framer-motion';
import { useRouter, usePathname } from 'next/navigation';
import { Loader2 } from 'lucide-react';

type TransitionPhase = 'idle' | 'covering' | 'covered' | 'revealing';

interface PageTransitionContextValue {
  navigateWithTransition: (href: string) => void;
}

const PageTransitionContext = createContext<PageTransitionContextValue>({
  navigateWithTransition: () => {},
});

export const usePageTransition = () => useContext(PageTransitionContext);

export function PageTransitionProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  const [phase, setPhase] = useState<TransitionPhase>('idle');
  const targetHref = useRef<string | null>(null);
  const [prevPathname, setPrevPathname] = useState(pathname);

  const startTransition = useCallback(
    (href: string) => {
      if (phase !== 'idle') return;
      targetHref.current = href;
      setPhase('covering');
    },
    [phase]
  );

  // Detect pathname change to trigger reveal (derived state during render)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    if (phase === 'covered') {
      setPhase('revealing');
    }
  }

  // Safety timeout — force reveal if server is slow
  useEffect(() => {
    if (phase !== 'covered') return;
    const timeout = setTimeout(() => setPhase('revealing'), 4000);
    return () => clearTimeout(timeout);
  }, [phase]);

  // Global click interception
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (phase !== 'idle') return;

      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (anchor.hasAttribute('download')) return;
      if (anchor.hasAttribute('data-no-transition')) return;

      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href) return;
      if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (href.startsWith('/cms')) return;

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === pathname && url.search === window.location.search) return;

        e.preventDefault();
        startTransition(url.pathname + url.search + url.hash);
      } catch {
        return;
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [phase, pathname, startTransition]);

  const handleAnimationComplete = useCallback(() => {
    if (phase === 'covering') {
      setPhase('covered');
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      router.push(targetHref.current!);
    } else if (phase === 'revealing') {
      setPhase('idle');
      targetHref.current = null;
    }
  }, [phase, router]);

  return (
    <PageTransitionContext.Provider value={{ navigateWithTransition: startTransition }}>
      {children}
      {phase !== 'idle' && (
        <motion.div
          className="fixed inset-0 z-[60] bg-white flex items-center justify-center"
          initial={{ y: '4%', opacity: 0 }}
          animate={
            phase === 'covering' || phase === 'covered'
              ? { y: '0%', opacity: 1 }
              : { y: '0%', opacity: 0 }
          }
          transition={{
            duration: phase === 'revealing' ? 0.3 : 0.35,
            ease: [0.65, 0, 0.35, 1],
          }}
          onAnimationComplete={handleAnimationComplete}
          style={{ willChange: 'transform, opacity' }}
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </motion.div>
      )}
    </PageTransitionContext.Provider>
  );
}
